import { Button, Heading } from '@/components/atoms';
import {
  AnalyticsChart,
  MetricCard,
  DataTable,
  AnalyticsEmptyState,
} from '@/components/molecules';
import type { ColumnDef } from '@tanstack/react-table';
import type { AnalyticsTransactions } from '@/types';
import type { TransactionUI } from '@/pages/dashboard/transactions/types';
import { Link } from 'react-router';
import { routes } from '@/lib/constants';
import { formatCurrency } from '@/lib/helpers';

interface TransactionsTabProps {
  showEmptyChart: boolean;
  showAnalyticsEmptyState: boolean;
  onRowClick: (tx: TransactionUI) => void;
  transactionData: TransactionUI[];
  transactionColumns: ColumnDef<TransactionUI>[];
  monthlySalesData: Array<{ month: string; sales: number }>;
  emptyChartData: Array<{ month: string; sales: number }>;
  chartConfig: {
    sales: { label: string; color: string };
  };
  analyticsData?: AnalyticsTransactions;
  topMonth?: string;
  topYear?: string;
  topBuyer?: string;
  topBuyerSubtitle?: string;
  txCountLabel?: string;
}

export const TransactionsTab = ({
  showEmptyChart,
  showAnalyticsEmptyState,
  onRowClick,
  transactionData,
  transactionColumns,
  monthlySalesData,
  emptyChartData,
  chartConfig,
  analyticsData,
  topMonth = '—',
  topYear = new Date().getFullYear().toString(),
  topBuyer = '—',
  topBuyerSubtitle = '—',
  txCountLabel = '0 transactions so far',
}: TransactionsTabProps) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <AnalyticsChart
            title="Total Transactions"
            amount={
              showEmptyChart
                ? '₦0.00'
                : formatCurrency(Number(analyticsData?.totalPayouts ?? 0))
            }
            showEmptyChart={showEmptyChart}
            chartData={monthlySalesData}
            emptyChartData={emptyChartData}
            chartConfig={chartConfig}
          />
        </div>
        <div className="space-y-4">
          <MetricCard
            title="Top year"
            value={topYear}
            subtitle={txCountLabel}
            icon="trending"
            showEmpty={showEmptyChart}
          />
          <MetricCard
            title="Top month"
            value={topMonth}
            subtitle={topYear}
            icon="calendar"
            showEmpty={showEmptyChart}
          />
          <MetricCard
            title="Top buyer"
            value={topBuyer}
            subtitle={topBuyerSubtitle}
            icon="user"
            showEmpty={showEmptyChart}
          />
        </div>
      </div>

      <div className="space-y-4">
        <Heading size="lg">Recent Transactions</Heading>
        {showAnalyticsEmptyState ? (
          <AnalyticsEmptyState message="You don't have any recent transactions" />
        ) : (
          <>
            <DataTable
              columns={transactionColumns}
              data={transactionData}
              onRowClick={onRowClick}
            />
            <div className="flex justify-center mt-4">
              <Button variant="outline" className="text-gray-600" asChild>
                <Link to={routes.transactions}>View transaction details</Link>
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
